import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { grantShares, revokeShares, searchCharacters, transferShares } from "@/lib/economy-api";
import { SHARE_MANAGER } from "@/constants/testIds";

const CharacterPicker = ({ selected, onSelect, placeholder }) => {
  const [query, setQuery] = useState(selected?.name ?? "");
  const [results, setResults] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2 || (selected && selected.name === query)) {
      setResults([]);
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        setResults(await searchCharacters(query.trim()));
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, selected]);

  return (
    <div className="relative">
      <input
        value={query}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          if (selected) onSelect(null);
        }}
        data-testid={SHARE_MANAGER.characterSearchInput}
        className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white outline-none focus:border-[#F0801F]/60"
      />
      {results.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full rounded-md border border-white/10 bg-[#1e1e23] shadow-lg">
          {results.map((c) => (
            <li key={c.guid}>
              <button
                type="button"
                data-testid={SHARE_MANAGER.characterSearchResult}
                onClick={() => {
                  onSelect(c);
                  setQuery(c.name);
                  setResults([]);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-white/80 hover:bg-white/[0.06]"
              >
                {c.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const ShareManagerDialog = ({ open, onClose, company, onChanged }) => {
  const [grantTarget, setGrantTarget] = useState(null);
  const [grantShareCount, setGrantShareCount] = useState(1);
  const [transferState, setTransferState] = useState(null); // { fromGuid, target, shares }
  const [busy, setBusy] = useState(false);

  if (!company) return null;

  const totalAssigned = company.shares_assigned ?? 0;

  const handleGrant = async () => {
    if (!grantTarget) {
      toast("Vyber postavu", { description: "Nejdřív vyhledej postavu, které chceš podíl přidělit." });
      return;
    }
    setBusy(true);
    try {
      await grantShares(company.id, { character_guid: grantTarget.guid, shares: Number(grantShareCount) });
      toast("Podíl přidělen", { description: `${grantTarget.name} nyní vlastní další ${grantShareCount} %.` });
      setGrantTarget(null);
      setGrantShareCount(1);
      onChanged();
    } catch (err) {
      toast("Nepodařilo se přidělit podíl", { description: err.response?.data?.detail ?? "Zkus to znovu." });
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (characterGuid, shares) => {
    setBusy(true);
    try {
      await revokeShares(company.id, { character_guid: characterGuid, shares });
      toast("Podíl odebrán");
      onChanged();
    } catch (err) {
      toast("Nepodařilo se odebrat podíl", { description: err.response?.data?.detail ?? "Zkus to znovu." });
    } finally {
      setBusy(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferState?.target) {
      toast("Vyber cílovou postavu");
      return;
    }
    setBusy(true);
    try {
      await transferShares(company.id, {
        from_character_guid: transferState.fromGuid,
        to_character_guid: transferState.target.guid,
        shares: Number(transferState.shares),
      });
      toast("Podíl převeden");
      setTransferState(null);
      onChanged();
    } catch (err) {
      toast("Převod se nezdařil", { description: err.response?.data?.detail ?? "Zkus to znovu." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        data-testid={SHARE_MANAGER.dialog}
        className="border-white/10 bg-[#17171b] text-white sm:max-w-xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            Podíly - {company.name}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Přiděleno {totalAssigned} % ze 100 %. Podíly patří postavám, ne účtům.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {company.shareholders.length === 0 && (
            <p className="text-sm text-white/50">Zatím nikdo nevlastní žádný podíl.</p>
          )}
          {company.shareholders.map((s) => (
            <div key={s.character_guid} className="rounded-md border border-white/10 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/80">
                  {s.character_name}{" "}
                  <span className="text-[#F0801F]">{s.shares} %</span>
                  {s.is_majority_owner && (
                    <span className="ml-2 rounded-full bg-[#F0801F]/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#F0801F]">
                      Většinový vlastník
                    </span>
                  )}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleRevoke(s.character_guid, s.shares)}
                    data-testid={SHARE_MANAGER.revokeButton}
                    className="rounded-md border border-white/20 px-3 py-1.5 text-xs uppercase tracking-wide text-white/70 hover:border-red-400/60 hover:text-red-300"
                  >
                    Odebrat vše
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setTransferState(
                        transferState?.fromGuid === s.character_guid
                          ? null
                          : { fromGuid: s.character_guid, target: null, shares: s.shares }
                      )
                    }
                    className="rounded-md border border-white/20 px-3 py-1.5 text-xs uppercase tracking-wide text-white/70 hover:border-[#F0801F]/60 hover:text-[#F0801F]"
                  >
                    Převést
                  </button>
                </div>
              </div>

              {transferState?.fromGuid === s.character_guid && (
                <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
                  <CharacterPicker
                    selected={transferState.target}
                    onSelect={(c) => setTransferState((prev) => ({ ...prev, target: c }))}
                    placeholder="Komu převést?"
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min={1}
                      max={s.shares}
                      value={transferState.shares}
                      onChange={(e) => setTransferState((prev) => ({ ...prev, shares: e.target.value }))}
                      className="w-24 rounded-md border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white outline-none focus:border-[#F0801F]/60"
                    />
                    <button
                      type="button"
                      disabled={busy}
                      onClick={handleTransfer}
                      data-testid={SHARE_MANAGER.transferButton}
                      className="flex-1 rounded-md bg-[#F0801F] py-2 text-xs font-600 uppercase tracking-wide text-[#141417] hover:bg-[#ff9438]"
                    >
                      Potvrdit převod
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {totalAssigned < 100 && (
          <div className="space-y-2 border-t border-white/10 pt-4">
            <p className="font-display text-xs uppercase tracking-[0.16em] text-white/50">
              Přidělit nový podíl ({100 - totalAssigned} % volných)
            </p>
            <CharacterPicker selected={grantTarget} onSelect={setGrantTarget} placeholder="Hledej postavu…" />
            <div className="flex gap-2">
              <input
                type="number"
                min={1}
                max={100 - totalAssigned}
                value={grantShareCount}
                onChange={(e) => setGrantShareCount(e.target.value)}
                data-testid={SHARE_MANAGER.sharesInput}
                className="w-24 rounded-md border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white outline-none focus:border-[#F0801F]/60"
              />
              <button
                type="button"
                disabled={busy}
                onClick={handleGrant}
                data-testid={SHARE_MANAGER.grantButton}
                className="flex-1 rounded-md bg-[#F0801F] py-2 text-xs font-600 uppercase tracking-wide text-[#141417] hover:bg-[#ff9438]"
              >
                Přidělit podíl
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
